// src/hooks/usePageScroll.ts
import { useEffect, useState } from "react";

export function usePageScroll(opts?: { stepRatio?: number; topResetOnMount?: boolean }) {
  const stepRatio = opts?.stepRatio ?? 0.85;
  const topResetOnMount = opts?.topResetOnMount ?? false;

  const [showJumpUp, setShowJumpUp] = useState(false);
  const [showJumpDown, setShowJumpDown] = useState(false);

  useEffect(() => {
    if (topResetOnMount) {
      // robust reset to top
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      const se = document.scrollingElement || document.documentElement;
      if (se) se.scrollTop = 0;
      const id = window.requestAnimationFrame(() => { window.scrollTo(0, 0); });
      return () => cancelAnimationFrame(id);
    }
  }, [topResetOnMount]);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      const max = (document.documentElement.scrollHeight - window.innerHeight) || 0;

      // Only show if the page is meaningfully taller than the viewport
      const tallEnough = (document.documentElement.scrollHeight - window.innerHeight) > 160;

      setShowJumpUp(tallEnough && y > 80);
      setShowJumpDown(tallEnough && (max - y) > 120);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  function jump(dir: "up" | "down") {
    const delta = Math.round(window.innerHeight * stepRatio);
    const y = window.scrollY || document.documentElement.scrollTop || 0;
    const target = dir === "up" ? Math.max(0, y - delta) : y + delta;
    window.scrollTo({ top: target, behavior: "smooth" });
  }

  return { showJumpUp, showJumpDown, jump };
}
